/**
 * Orange top strip for a contiguous crew-override run: draggable left/right edges to
 * expand/contract which aspect days use the override crew (clamped to the bar).
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { PLANNING_TOOLTIP_MESSAGES } from "../../../../config";
import { SCHEDULE_WORKER_OVERRIDE_STRIP } from "../style";
import { toLocalCalendarDay, toLocalDateKey } from "../../../../utils/datetime/core";

/**
 * @param {number} cellWidth
 * @param {number} barWidth
 * @param {number} colCount columns in this bar strip
 * @param {number} startCol inclusive, 0-based within strip
 * @param {number} endCol inclusive
 */
function runBoundsPx(cellWidth, barWidth, colCount, startCol, endCol) {
  let left = 0;
  for (let i = 0; i < startCol; i += 1) {
    const isLast = i === colCount - 1;
    const w = isLast ? Math.max(0, barWidth - i * cellWidth) : cellWidth;
    left += w;
  }
  let width = 0;
  for (let i = startCol; i <= endCol; i += 1) {
    const isLast = i === colCount - 1;
    const w = isLast ? Math.max(0, barWidth - i * cellWidth) : cellWidth;
    width += w;
  }
  return { left, width };
}

/**
 * Return `[earlier, later]` local calendar days for an inclusive override range.
 *
 * @param {Date|string|number} a
 * @param {Date|string|number} b
 * @returns {[Date, Date]}
 */
function orderDayRange(a, b) {
  const da = toLocalCalendarDay(a);
  const db = toLocalCalendarDay(b);
  return toLocalDateKey(da) <= toLocalDateKey(db) ? [da, db] : [db, da];
}

/**
 * @param {Object} props
 * @param {number} props.cellWidth
 * @param {number} props.barWidth
 * @param {number} props.firstVis visibleDays index of first column
 * @param {number} props.colCount
 * @param {Date[]} props.visibleDays
 * @param {{ startCol: number, endCol: number }} props.run
 * @param {number[]} props.overrideWorkerIds crew to apply for days in range
 * @param {string|number} props.blockId
 * @param {boolean} [props.clippedStart] True when this strip’s **left edge** is flush with the viewport edge *and*
 *   the underlying block continues earlier off-screen (suppress misleading handles/margins on that edge).
 * @param {boolean} [props.clippedEnd] True when this strip’s **right edge** is flush with the viewport edge *and*
 *   the underlying block continues later off-screen.
 * @param {boolean} [props.startHandleLocked] True when start handle day is dispatch-locked.
 * @param {boolean} [props.endHandleLocked] True when end handle day is dispatch-locked.
 * @param {string} [props.lockedHandleTitle] Native tooltip shown on disabled handle placeholders.
 * @param {(p: { blockId: string|number, previousStartDay: Date, previousEndDay: Date, nextStartDay: Date, nextEndDay: Date, overrideWorkerIds: number[] }) => void|Promise<void>} props.onCommit
 * @param {(clientX: number) => number|null} props.resolveRelColumnFromClientX 0..colCount-1
 */
export default function OverrideStrip({
  cellWidth,
  barWidth,
  firstVis,
  colCount,
  visibleDays,
  run,
  overrideWorkerIds,
  blockId,
  clippedStart = false,
  clippedEnd = false,
  startHandleLocked = false,
  endHandleLocked = false,
  lockedHandleTitle = PLANNING_TOOLTIP_MESSAGES.draggingDisabledDueToLockedDay,
  onCommit,
  resolveRelColumnFromClientX,
}) {
  const [preview, setPreview] = useState(null);
  /** @type {{ edge: 'start'|'end', pointerId: number, handleEl: Element, orig: { startCol: number, endCol: number } } | null} */
  const [dragSession, setDragSession] = useState(null);
  const previewRef = useRef(null);
  /** Prevents starting a new resize while an async `onCommit` is still persisting + parent state is catching up. */
  const commitInFlightRef = useRef(false);
  /** Avoid effect churn when parent passes new array refs each render. */
  const commitCtxRef = useRef({
    blockId,
    onCommit,
    overrideWorkerIds,
    visibleDays,
    firstVis,
  });
  commitCtxRef.current = {
    blockId,
    onCommit,
    overrideWorkerIds,
    visibleDays,
    firstVis,
  };

  const startCol = preview?.startCol ?? run.startCol;
  const endCol = preview?.endCol ?? run.endCol;

  const { left, width } = runBoundsPx(
    cellWidth,
    barWidth,
    colCount,
    startCol,
    endCol
  );

  const insetStart = !clippedStart && startCol === 0;
  const insetEnd = !clippedEnd && endCol === colCount - 1;

  useEffect(() => {
    if (!dragSession) {
      return undefined;
    }
    const { edge, pointerId, handleEl, orig } = dragSession;

    const onMove = (e) => {
      if (e.pointerId !== pointerId) {
        return;
      }
      const col = resolveRelColumnFromClientX(e.clientX);
      if (col == null) {
        return;
      }
      if (edge === "start") {
        const nextStart = Math.max(0, Math.min(col, orig.endCol));
        const next = { startCol: nextStart, endCol: orig.endCol };
        previewRef.current = next;
        setPreview(next);
      } else {
        const nextEnd = Math.min(colCount - 1, Math.max(col, orig.startCol));
        const next = { startCol: orig.startCol, endCol: nextEnd };
        previewRef.current = next;
        setPreview(next);
      }
    };

    const finish = (e) => {
      if (e.pointerId !== pointerId) {
        return;
      }
      try {
        handleEl.releasePointerCapture(pointerId);
      } catch {
        /* noop */
      }
      const fin = previewRef.current ?? orig;

      // Tear down the window-level drag listeners immediately; further moves should not affect preview.
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      // End the drag session (handles go idle), but keep `preview` until persistence finishes — otherwise the
      // strip snaps back to the pre-drag `run` for a frame while `workScopeAspects`/bars recompute.
      setDragSession(null);

      if (fin.startCol === orig.startCol && fin.endCol === orig.endCol) {
        setPreview(null);
        previewRef.current = null;
        return;
      }
      const { visibleDays: vd, firstVis: fv, onCommit: oc, blockId: bid, overrideWorkerIds: owi } =
        commitCtxRef.current;
      const dayForCol = (c) => vd[fv + c] ?? null;
      const d0 = dayForCol(orig.startCol);
      const d1 = dayForCol(orig.endCol);
      const n0 = dayForCol(fin.startCol);
      const n1 = dayForCol(fin.endCol);
      if (!d0 || !d1 || !n0 || !n1) {
        setPreview(null);
        previewRef.current = null;
        return;
      }
      const [previousStartDay, previousEndDay] = orderDayRange(d0, d1);
      const [nextStartDay, nextEndDay] = orderDayRange(n0, n1);
      void (async () => {
        commitInFlightRef.current = true;
        try {
          // `onCommit` is typically async (network + React state). Awaiting it keeps the preview geometry stable
          // until the parent-derived `run` catches up, avoiding a one-frame “flash” of the old strip range.
          await Promise.resolve(
            oc?.({
              blockId: bid,
              previousStartDay,
              previousEndDay,
              nextStartDay,
              nextEndDay,
              overrideWorkerIds: owi,
            })
          );
        } catch {
          /* Parent should surface failures; keep UI stable here. */
        } finally {
          commitInFlightRef.current = false;
          setPreview(null);
          previewRef.current = null;
        }
      })();
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
    };
  }, [colCount, dragSession, resolveRelColumnFromClientX]);

  const beginEdge = useCallback(
    (edge) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (commitInFlightRef.current) {
        return;
      }
      const orig = { startCol: run.startCol, endCol: run.endCol };
      previewRef.current = orig;
      setPreview(orig);
      setDragSession({
        edge,
        pointerId: e.pointerId,
        handleEl: e.currentTarget,
        orig,
      });
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [run.endCol, run.startCol]
  );

  return (
    <div
      data-schedule-override-strip=""
      className={`pointer-events-none absolute top-1 z-[25] h-1 ${SCHEDULE_WORKER_OVERRIDE_STRIP.barBgClass} ${clippedStart && clippedEnd ? "rounded-none" : clippedStart ? "rounded-tr-sm" : clippedEnd ? "rounded-tl-sm" : "rounded-t"}`}
      style={{
        left,
        width: width - (insetStart ? 8 : 0) - (insetEnd ? 8 : 0),
        marginLeft: insetStart ? 8 : 0,
        marginRight: insetEnd ? -8 : 0,
      }}
      role="presentation"
    >
      {!clippedStart ? (
        startHandleLocked ? (
          <div
            title={lockedHandleTitle}
            aria-label={lockedHandleTitle}
            className={`pointer-events-auto absolute left-0 -top-1 z-[35] h-3 w-1.5 touch-none rounded-sm border-0 opacity-60 ${SCHEDULE_WORKER_OVERRIDE_STRIP.handleBgClass}`}
          />
        ) : (
          <button
            type="button"
            data-schedule-override-resize-handle=""
            aria-label="Resize crew override start"
            className={`pointer-events-auto absolute left-0 -top-1 z-[35] h-3 w-1.5 cursor-ew-resize touch-none rounded-sm border-0 ${SCHEDULE_WORKER_OVERRIDE_STRIP.handleBgClass} ${SCHEDULE_WORKER_OVERRIDE_STRIP.handleHoverBgClass}`}
            style={{ touchAction: "none" }}
            onPointerDown={beginEdge("start")}
          />
        )
      ) : null}
      {!clippedEnd ? (
        endHandleLocked ? (
          <div
            title={lockedHandleTitle}
            aria-label={lockedHandleTitle}
            className={`pointer-events-auto absolute right-0 -top-1 z-[35] h-3 w-1.5 touch-none rounded-sm border-0 opacity-60 ${SCHEDULE_WORKER_OVERRIDE_STRIP.handleBgClass}`}
          />
        ) : (
          <button
            type="button"
            data-schedule-override-resize-handle=""
            aria-label="Resize crew override end"
            className={`pointer-events-auto absolute right-0 -top-1 z-[35] h-3 w-1.5 cursor-ew-resize touch-none rounded-sm border-0 ${SCHEDULE_WORKER_OVERRIDE_STRIP.handleBgClass} ${SCHEDULE_WORKER_OVERRIDE_STRIP.handleHoverBgClass}`}
            style={{ touchAction: "none" }}
            onPointerDown={beginEdge("end")}
          />
        )
      ) : null}
    </div>
  );
}
